import { toggleChip } from '../../lib/profiles';

/**
 * Multi-select chips for a small controlled vocabulary (allergens, diets,
 * appliances). Each chip is a toggle button (`aria-pressed`), 44 px tall.
 */
export function ChipToggles<T extends string>({
  label,
  hint,
  keys,
  labels,
  value,
  onChange,
  disabled = false,
  tone = 'brand',
}: {
  label: string;
  hint?: string;
  keys: readonly T[];
  labels: Record<T, string>;
  value: readonly T[];
  onChange: (next: T[]) => void;
  disabled?: boolean;
  /** `danger` for allergen-style lists where "on" means "avoid". */
  tone?: 'brand' | 'danger';
}) {
  const on =
    tone === 'danger' ? 'border-fail bg-fail text-white' : 'border-brand bg-brand text-white';
  return (
    <fieldset className="flex flex-col gap-2" disabled={disabled}>
      <legend className="text-sm font-medium text-ink">{label}</legend>
      {hint ? <p className="-mt-1 text-xs text-muted">{hint}</p> : null}
      <ul className="flex flex-wrap gap-2">
        {keys.map((key) => {
          const pressed = value.includes(key);
          return (
            <li key={key}>
              <button
                type="button"
                aria-pressed={pressed}
                onClick={() => onChange(toggleChip(value, key, keys))}
                className={`min-h-11 rounded-full border px-3.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  pressed
                    ? on
                    : 'border-line bg-surface text-ink hover:bg-brand-soft hover:text-brand'
                }`}
              >
                {pressed ? <span aria-hidden>✓ </span> : null}
                {labels[key]}
              </button>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

/** Small read-only chips (row summaries). */
export function MiniChips({
  items,
  tone = 'neutral',
  label,
}: {
  items: readonly string[];
  tone?: 'neutral' | 'danger' | 'brand';
  label: string;
}) {
  if (items.length === 0) return null;
  const cls =
    tone === 'danger'
      ? 'bg-red-50 text-fail'
      : tone === 'brand'
        ? 'bg-brand-soft text-brand'
        : 'bg-cream text-ink';
  return (
    <ul aria-label={label} className="flex flex-wrap gap-1">
      {items.map((t) => (
        <li key={t} className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
          {t}
        </li>
      ))}
    </ul>
  );
}
